/**
 * Windows Update for Business Reporting integration card.
 *
 * Originally lived as a dedicated tab inside OS Patch Management > Reports
 * (now Discover). Moved to Settings > Integrations on 2026-06-15 per the
 * scott/ospatch-ui Phase 1 reshape — keeps all third-party integration
 * configuration together with Azure AD.
 *
 * Self-contained: loads its own settings on mount, owns the save / disconnect
 * flow, no props required.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Database,
  Shield,
  FileText,
  CheckCircle,
} from "lucide-react";

const WufBIntegrationCard = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ subscription_id: "", workspace_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/os-patch/wufb-integration/settings`)
      .then((res) => {
        if (cancelled) return;
        setSettings(res.data || {});
        setForm({
          subscription_id: res.data?.subscription_id || "",
          workspace_id: res.data?.workspace_id || "",
        });
      })
      .catch(() => {
        if (!cancelled) setSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/os-patch/wufb-integration/settings`,
        form
      );
      setSettings(res.data || form);
      toast.success("WUfB integration saved");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to save WUfB integration"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.delete(`${API}/os-patch/wufb-integration/settings`);
      setSettings({});
      setForm({ subscription_id: "", workspace_id: "" });
      toast.success("WUfB integration disconnected");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to disconnect WUfB"
      );
    }
  };

  const isConnected = !!settings?.is_connected;

  return (
    <Card data-testid="settings-wufb-integration-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <Database className="h-5 w-5" />
          Windows Update for Business Reporting
        </CardTitle>
        <CardDescription>
          Connect ALICE to your Log Analytics workspace to query Windows Update
          for Business client telemetry (security update status, client state,
          policy compliance, delivery optimisation).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3" data-testid="wufb-status-row">
          <div
            className={`w-3 h-3 rounded-full ${
              isConnected ? "bg-green-500" : "bg-red-500"
            }`}
          />
          <span className="font-medium">
            {isConnected ? "Connected to Log Analytics" : "Not Connected"}
          </span>
          {settings?.connected_at && (
            <span className="text-sm text-muted-foreground">
              (Connected: {new Date(settings.connected_at).toLocaleDateString()})
            </span>
          )}
        </div>

        <div className="grid gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Azure Subscription ID</label>
            <Input
              data-testid="wufb-subscription-id-input"
              value={form.subscription_id}
              onChange={(e) =>
                setForm({ ...form, subscription_id: e.target.value })
              }
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Log Analytics Workspace ID
            </label>
            <Input
              data-testid="wufb-workspace-id-input"
              value={form.workspace_id}
              onChange={(e) =>
                setForm({ ...form, workspace_id: e.target.value })
              }
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="font-mono text-sm"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || !form.subscription_id || !form.workspace_id}
              data-testid="wufb-save-btn"
            >
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
            {isConnected && (
              <Button
                variant="outline"
                onClick={handleDisconnect}
                data-testid="wufb-disconnect-btn"
                className="border-red-500/50 text-red-600 hover:bg-red-50"
              >
                Disconnect
              </Button>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-4 bg-white/10">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Required Permissions
          </h4>
          <p className="text-sm mb-3">
            The ALICE Enterprise Application needs the{" "}
            <strong>Log Analytics Reader</strong> role on your Log Analytics
            workspace.
          </p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>
              Go to Azure Portal → Log Analytics workspace → Access control
              (IAM)
            </li>
            <li>Click "Add role assignment"</li>
            <li>Select "Log Analytics Reader" role</li>
            <li>
              Under "Members", select "User, group, or service principal"
            </li>
            <li>Search for "ALICE" and select the Enterprise Application</li>
            <li>Click "Review + assign"</li>
          </ol>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border rounded-lg p-4 bg-white/10">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              What Data Will Be Available
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                "Security update status",
                "Client state (downloading, installing, pending reboot)",
                "Last Windows Update scan",
                "Update health and errors",
                "Policy compliance status",
                "Delivery optimization status",
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="border rounded-lg p-4 bg-white/10">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Database className="h-4 w-4" />
              Log Analytics Tables Used
            </h4>
            <div className="flex flex-wrap gap-2">
              {[
                "UCClient",
                "UCClientUpdateStatus",
                "UCUpdateAlert",
                "UCDeviceAlert",
                "UCDOStatus",
                "UCServiceUpdateStatus",
              ].map((table) => (
                <span
                  key={table}
                  className="px-2.5 py-1 bg-purple-500/15 text-purple-700 dark:text-purple-300 rounded text-xs font-mono"
                >
                  {table}
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              These tables are populated by Windows Update for Business reports
              in your Log Analytics workspace.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WufBIntegrationCard;
